import { DataSet, DataView } from 'vis-network/standalone/esm/vis-network'
import { validateNode, validateEdge, nodeToVisId, edgeToVisIds, nodeToVisNode, edgeToVisEdge } from './visjs_object_conversion'
import { isDeepEqual } from './utils'

function constructNodesAndEdgesDictionaries(nodes, edges) {
  let newNodes = {};
  let newEdges = {};
  for (const node of nodes) {
    try { validateNode(node); } catch(err) { console.warn("[athenalib:graph] Ignoring invalid node: " + err); continue; }

    const id = nodeToVisId(node);
    if (id in newNodes) {
      console.warn("[athenalib:graph] Ignoring duplicate node in graph: type =", node.type, " ; value =", node.value);
      continue;
    }
    newNodes[id] = node;
  }

  for (const edge of edges) {
    try { validateEdge(edge); } catch(err) { console.warn("[athenalib:graph] Ignoring invalid edge: " + err); continue; }

    const { from, to, id } = edgeToVisIds(edge);
    if (!(from in newNodes) || !(to in newNodes)) {
      console.warn("[athenalib:graph] Ignoring edge with reference to non-existing node: ", edge);
      continue;
    }
    // TODO: decide how to handle bidirectional edges
    if (id in newEdges) {
      console.warn("[athenalib:graph] Ignoring duplicate edge: ", edge);
      continue;
    }
    newEdges[id] = edge;
  }
  return { nodesDict: newNodes, edgesDict: newEdges };
}

export class VisjsDatasetsHandler {
  constructor(enableFilters) {
    this.enableFilters = enableFilters;
    // visjs input
    this.nodesDS = new DataSet([]);
    this.edgesDS = new DataSet([]);
    this.network = null;

    if (enableFilters) {
      this.fNodeFilter = null;
      this.fEdgeFilter = null;
      const self = this;
      this.nodesDV = new DataView(this.nodesDS, {filter: (node) => {
        if (self.fNodeFilter == null) return true;
        if (!self.crtNodes[node.id]) return false;
        return self.fNodeFilter(self.crtNodes[node.id]);
      }});
      this.edgesDV = new DataView(this.edgesDS, {filter: (edge) => {
        if (self.fEdgeFilter == null) return true;
        if (!self.crtEdges[edge.id]) return false;
        return self.fEdgeFilter(self.crtEdges[edge.id]);
      }});
      this.networkData = {nodes: this.nodesDV, edges: this.edgesDV};
    } else {
      this.networkData = {nodes: this.nodesDS, edges: this.edgesDS};
    }

    // {id: user_provided_object} dictionaries synchronized with the visjs input from above
    this.crtNodes = {}
    this.crtEdges = {}

    this.changedNodeIds = {}
    this.changedEdgeIds = {}
  }

  setFilter(nodeFilterFunc, edgeFilterFunc) {
    if (!this.enableFilters) return;
    this.fNodeFilter = nodeFilterFunc;
    this.fEdgeFilter = edgeFilterFunc;
    this.nodesDV.refresh();
    this.edgesDV.refresh();
  }

  getNode(id) {
    return this.crtNodes[id];
  }
  getEdge(id) {
    return this.crtEdges[id];
  }

  setNodesAndEdges(nodes, edges, {setNodesPosition}) {
    const t0 = Date.now();
    if (!nodes || !edges) {
      this.edgesDS.clear();
      this.nodesDS.clear();
      this.crtNodes = {};
      this.crtEdges = {};
      return;
    }
    const { nodesDict: newNodes, edgesDict: newEdges } = constructNodesAndEdgesDictionaries(nodes, edges);

    const nodeIdsNew = [], nodeIdsDeleted = [], nodeIdsModified = [];
    const edgeIdsNew = [], edgeIdsDeleted = [], edgeIdsModified = [];

    const nodePositionUpdates = [];

    for (let id in newNodes) {
      if (!(id in this.crtNodes)) { nodeIdsNew.push(id); continue; }
      if (!isDeepEqual(newNodes[id], this.crtNodes[id])) { nodeIdsModified.push(id); continue; }
    }
    for (let id in this.crtNodes) {
      if (!(id in newNodes)) { nodeIdsDeleted.push(id); continue; }
    }
    for (let id in newEdges) {
      if (!(id in this.crtEdges)) { edgeIdsNew.push(id); continue; }
      if (!isDeepEqual(newEdges[id], this.crtEdges[id])) { edgeIdsModified.push(id); continue; }
    }
    for (let id in this.crtEdges) {
      if (!(id in newEdges)) { edgeIdsDeleted.push(id); continue; }
    }

    // remove deleted edges and nodes
    this.edgesDS.remove(edgeIdsDeleted); // DataSet can remove a list of ids
    this.nodesDS.remove(nodeIdsDeleted);

    // add + modify (DataSet.update() can take a list to update or create)
    const visNodeUpdates = [];
    for (let id of nodeIdsNew) {
      const visNode = nodeToVisNode(newNodes[id]);
      if (newNodes[id].position) {
        nodePositionUpdates.push({id, pos: newNodes[id].position});
      }
      visNodeUpdates.push(visNode);
    }
    for (let id of nodeIdsModified) {
      const visNode = nodeToVisNode(newNodes[id]);
      visNodeUpdates.push(visNode);
    }
    this.nodesDS.update(visNodeUpdates);

    nodePositionUpdates.forEach(({id, pos}) => {
      // More efficient `this.network.moveNode(id, pos.x, pos.y);`
      // the original code sets a timeout for each moved node, which can impact performance
      if (this.network.body.nodes[id] !== undefined) {
        this.network.body.nodes[id].x = Number(pos.x);
        this.network.body.nodes[id].y = Number(pos.y);
      }
    });
    setTimeout(() => {
      this.network.body.emitter.emit("startSimulation");
    }, 0);



    const edgeIdsToUpdateOrCreate = [...edgeIdsNew, ...edgeIdsModified];
    const visEdgeUpdates = [];
    for (let id of edgeIdsToUpdateOrCreate) {
      const visEdge = edgeToVisEdge(newEdges[id]);
      visEdgeUpdates.push(visEdge);
    }
    this.edgesDS.update(visEdgeUpdates);

    this.crtNodes = newNodes;
    this.crtEdges = newEdges;

    this.changedNodeIds = {};
    this.changedEdgeIds = {};
    nodeIdsModified.forEach(id => {this.changedNodeIds[id] = 1;});
    edgeIdsModified.forEach(id => {this.changedEdgeIds[id] = 1;});
    // console.log(`Network update (${Date.now() - t0}ms) (setPos=${setNodesPosition}) Nodes: ${nodeIdsNew.length} new, ${nodeIdsModified.length} mod, ${nodeIdsDeleted.length} del.   Edges: ${edgeIdsNew.length} new, ${edgeIdsModified.length} mod, ${edgeIdsDeleted.length} del.`)
  }

  getNodesWithPos(networkPositions) {
    const ret = [];
    for (let nodeId in this.crtNodes) {
      const node = {...this.crtNodes[nodeId]}
      if (nodeId in networkPositions) {
        node.position = {x: networkPositions[nodeId].x, y: networkPositions[nodeId].y};
      }
      ret.push(node);
    }
    return ret;
  }
}
